import { useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/auth/store';
import {
  useCreateVoiceWebhook,
  useVoiceWebhookBodyParameters,
} from '../api';
import { buildInitialBodyTable } from '../payload';
import { VoiceWebhookForm, type VoiceWebhookFormValues } from './VoiceWebhookForm';

export function AddVoiceWebhookPage() {
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.userId);
  const isSubOrSuper = useAuthStore((s) => s.isSubOrSuperAdmin());
  const create = useCreateVoiceWebhook();
  const bodyParams = useVoiceWebhookBodyParameters();

  const initial = useMemo<VoiceWebhookFormValues>(
    () => ({
      webhookName: '',
      url: '',
      event: '',
      markAsDefault: 0,
      headers: { 'Content-Type': 'application/json' },
      bodyTable: buildInitialBodyTable(bodyParams.data ?? []),
    }),
    [bodyParams.data],
  );

  // Super-admin / sub-super-admin are not allowed to add webhooks.
  if (isSubOrSuper) return <Navigate to="/webhook-list" replace />;

  // The form is keyed on the body-table length so re-mounts pick up the
  // populated body params once the lookup query resolves.
  const formKey = `${initial.bodyTable.length}`;

  return (
    <VoiceWebhookForm
      key={formKey}
      mode="create"
      initialValues={initial}
      isPending={create.isPending}
      onSubmit={(payload) => {
        create.mutate(
          {
            ...payload,
            userId: Number(userId),
          },
          {
            onSuccess: (res) => {
              if (res?.message === 'Parameters Incorrect') {
                toast.error(res.message);
                return;
              }
              toast.success(res?.message ?? 'Webhook created');
              navigate('/webhook-list');
            },
            onError: () => toast.error('Could not create the webhook.'),
          },
        );
      }}
    />
  );
}
